import React, { useEffect, useState } from "react";
import { IScheduledPush } from "../../../../../../interfaces/scheduledPush.interface";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Radio,
  Select,
  Space,
  notification,
} from "antd";
import { RangePickerProps } from "antd/lib/date-picker";
import { convertToDutchTimeZone } from "../../../../../../utils/convertTimezone";
import {
  IEmailNotification,
  NotificationType,
} from "../../../../../../interfaces/notification.interface";
import { IEmailTemplateSummary } from "../../../../../../interfaces/emailTemplate.interface";
import { getEmailTemplatesServer } from "../../../../../email-templates/emailTemplatesApi";
import { LOCALE_LABELS } from "../../../../../email-templates/emailTemplatesHelpers";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
interface IPropsNotificationStartForm {
  scheduledPush: IScheduledPush;
  onSubmit: (props: IScheduledPush) => void;
}

const ALLOWED_PARAMS: Record<
  string,
  { label: string; type: "date" | "time" | "text" }
> = {
  date: { label: "Date", type: "date" },
  startTime: { label: "Start Time", type: "time" },
  endTime: { label: "End Time", type: "time" },
  subject: { label: "Subject", type: "text" },
  paragraphOne: { label: "Paragraph 1", type: "text" },
  paragraphTwo: { label: "Paragraph 2", type: "text" },
};

const isBroadcastable = (template: { params: string[] }): boolean =>
  template.params.every((param) => param in ALLOWED_PARAMS);

const NotificationFieldLayout = {
  labelCol: { span: 12 },
  wrapperCol: { span: 12 },
};

const NOTIFICATION_TYPES_CONFIG: {
  label: string;
  value: NotificationType;
}[] = [
  { label: "Push", value: "NOTIFICATION" },
  { label: "In-App", value: "INAPP" },
  { label: "Email", value: "EMAIL" },
];

export default function NotificationStartForm({
  scheduledPush,
  onSubmit,
}: IPropsNotificationStartForm) {
  const [form] = Form.useForm();
  const randomUUID = uuidv4();
  const [currentType, setCurrentType] = useState(
    scheduledPush?.notification?.type
  );
  const [templates, setTemplates] = useState<IEmailTemplateSummary[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | undefined>(
    (scheduledPush?.notification as IEmailNotification)?.template
  );

  useEffect(() => {
    getEmailTemplatesServer()
      .then((all) => setTemplates(all.filter(isBroadcastable)))
      .catch(() => setTemplates([]));
  }, []);

  const selectedTemplate = templates.find((t) => t.slug === selectedSlug);
  const localeOptions = (selectedTemplate?.locales ?? []).map((locale) => ({
    value: locale,
    label: LOCALE_LABELS[locale] ?? locale,
  }));
  const templateParams = selectedTemplate?.params ?? [];

  useEffect(() => {
    if (scheduledPush) {
      form.setFieldsValue({
        timestamp:
          scheduledPush.timestamp && dayjs(scheduledPush.timestamp).isValid()
            ? dayjs(scheduledPush.timestamp)
            : "",
        notification: scheduledPush.notification,
      });
    }
  }, []);

  const handleSubmit = (
    values: IScheduledPush & { timestamp: dayjs.Dayjs } & {
      notification: { expiration?: any };
    }
  ) => {
    values.notification.analyticsIdentifier = randomUUID;
    if (values.notification.type === "INAPP" && values.notification.expiration)
      values.notification.expiration =
        values.notification.expiration?.toISOString();
    let scheduledPush = {
      ...values,
      timestamp: values.timestamp.toISOString(),
    };

    console.log("Schd", scheduledPush);
    onSubmit(scheduledPush);
  };

  const disabledDate: RangePickerProps["disabledDate"] = (selected) => {
    // Can not select days before today
    if (!selected || !selected.isValid()) return false;
    let selectedDutchDate = convertToDutchTimeZone(selected.toISOString());
    let currentDutchDate = convertToDutchTimeZone(new Date());
    return (
      selectedDutchDate &&
      selectedDutchDate.getDate() < currentDutchDate.getDate()
    );
  };
  const disabledTime: RangePickerProps["disabledTime"] = (_, type) => {
    return {};
  };

  const onTypeChange = (event: any) => {
    const value: NotificationType = event?.target?.value;
    setCurrentType(value);
  };
  const onEmailTemplateChange = (slug: string) => {
    setSelectedSlug(slug);
    form.setFieldValue(["notification", "locale"], undefined);
  };

  return (
    <div>
      <Form form={form} onFinish={handleSubmit} style={{ width: "100%" }}>
        <Form.Item
          name="timestamp"
          label="Date & Time"
          rules={[{ required: true, message: "Timestamp is required" }]}
          help="Shown as local time zone"
          wrapperCol={{ ...NotificationFieldLayout.wrapperCol }}
          labelCol={{ ...NotificationFieldLayout.labelCol }}
          labelAlign="left"
          style={{ marginBottom: "10px" }}
        >
          <DatePicker
            showTime
            format="DD-MM-YYYY HH:mm:ss"
            // disabledDate={disabledDate}
            //@ts-ignore
            disabledTime={disabledTime}
            showSecond={false}
            showNow={false}
          />
        </Form.Item>
        <Input.Group>
          <Form.Item
            name={["notification", "type"]}
            // noStyle
            rules={[{ required: true, message: "Type is required" }]}
            label={"Type"}
            wrapperCol={{ ...NotificationFieldLayout.wrapperCol }}
            labelCol={{ ...NotificationFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Radio.Group
              defaultValue="a"
              buttonStyle="solid"
              onChange={onTypeChange}
            >
              {NOTIFICATION_TYPES_CONFIG.map((g) => (
                <Radio value={g.value}>{g.label}</Radio>
              ))}
            </Radio.Group>
          </Form.Item>
          {currentType !== "EMAIL" ? (
            <>
              <Form.Item
                name={["notification", "title"]}
                // noStyle
                rules={[{ required: true, message: "Title is required" }]}
              >
                <Input placeholder="title" />
              </Form.Item>
              <Form.Item
                name={["notification", "description"]}
                // noStyle
                rules={[{ required: true, message: "Description is required" }]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Description"
                  maxLength={200}
                />
              </Form.Item>
              <Form.Item name={["notification", "analyticsIdentifier"]}>
                <Input placeholder={randomUUID} disabled />
              </Form.Item>
              {/* {form.getFieldValue("notification")?.type === "INAPP" && ( */}
              <Form.Item shouldUpdate>
                {() => {
                  return (
                    <Form.Item
                      name={["notification", "expiration"]}
                      // rules={[
                      //   { required: true, message: "Expiration is required" },
                      // ]}
                    >
                      <DatePicker
                        showTime
                        format="DD-MM-YYYY HH:mm:ss"
                        disabled={
                          form.getFieldValue("notification")?.type !== "INAPP"
                        }
                        disabledDate={disabledDate}
                        //@ts-ignore
                        disabledTime={disabledTime}
                        showSecond={false}
                        showNow={false}
                        placeholder="Select expiration date"
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name={["notification", "template"]}
                label={"Template"}
                rules={[{ required: true, message: "Template is required" }]}
                wrapperCol={{ ...NotificationFieldLayout.wrapperCol }}
                labelCol={{ ...NotificationFieldLayout.labelCol }}
                labelAlign="left"
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select a template"
                  onChange={onEmailTemplateChange}
                  options={templates.map((t) => ({
                    value: t.slug,
                    label: t.slug,
                  }))}
                ></Select>
              </Form.Item>

              <Form.Item
                name={["notification", "locale"]}
                label={"Language"}
                rules={[{ required: true, message: "Language is required" }]}
                wrapperCol={{ ...NotificationFieldLayout.wrapperCol }}
                labelCol={{ ...NotificationFieldLayout.labelCol }}
                labelAlign="left"
              >
                <Select
                  placeholder="Select a language"
                  disabled={!selectedSlug}
                  options={localeOptions}
                ></Select>
              </Form.Item>

              {templateParams.map((param) => {
                const meta = ALLOWED_PARAMS[param];
                return (
                  <Form.Item
                    key={param}
                    name={["notification", "data", param]}
                    label={meta.label}
                    rules={[
                      { required: true, message: `${meta.label} is required` },
                    ]}
                    wrapperCol={{ ...NotificationFieldLayout.wrapperCol }}
                    labelCol={{ ...NotificationFieldLayout.labelCol }}
                    labelAlign="left"
                  >
                    {meta.type === "text" ? (
                      <Input.TextArea rows={2} placeholder={meta.label} />
                    ) : (
                      <Input type={meta.type} />
                    )}
                  </Form.Item>
                );
              })}
            </>
          )}
        </Input.Group>
        <Form.Item>
          <Button type="primary" htmlType="submit" style={{ float: "right" }}>
            Next
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
